import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import type { AuthStatus } from '@/types/auth'
import YubiKeyVisual from '@/components/YubiKeyVisual'
import AuthStatusDisplay from '@/components/AuthStatus'
import { useWebAuthn } from '@/hooks/useWebAuthn'

function AuthPage() {
  const navigate = useNavigate()
  const { authenticate, isSupported } = useWebAuthn()
  const [status, setStatus] = useState<AuthStatus>('waiting')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string>()
  const hasRun = useRef(false)

  const runAuthentication = useCallback(async () => {
    try {
      // Step 1: Detect YubiKey (waiting state)
      setStatus('waiting')

      if (isSupported) {
        // Real WebAuthn flow
        setStatus('touching')
        const result = await authenticate()

        if (result.success) {
          setStatus('verifying')
          // Animate progress bar during backend verification
          for (let i = 0; i <= 100; i += 5) {
            setProgress(i)
            await new Promise((r) => setTimeout(r, 30))
          }
          setStatus('success')
          setTimeout(() => navigate('/dashboard', { state: { user: result.user } }), 1000)
        } else {
          setStatus('error')
          setError(result.error)
        }
      } else {
        // Simulated flow (fallback for demos without WebAuthn)
        await new Promise((r) => setTimeout(r, 2000))
        setStatus('touching')
        await new Promise((r) => setTimeout(r, 1500))
        setStatus('verifying')
        for (let i = 0; i <= 100; i += 5) {
          setProgress(i)
          await new Promise((r) => setTimeout(r, 30))
        }
        await new Promise((r) => setTimeout(r, 300))
        setStatus('success')
        setTimeout(() => navigate('/dashboard'), 1000)
      }
    } catch (err: any) {
      console.error('Authentication error details:', err)
      setStatus('error')
      // Show full error details if available for debugging
      const errorMessage = err.message || 'Authentication failed'
      const errorDetails = err.code ? ` (${err.code})` : ''
      setError(`${errorMessage}${errorDetails}`)
    }
  }, [authenticate, isSupported, navigate])

  useEffect(() => {
    if (!hasRun.current) {
      hasRun.current = true
      runAuthentication()
    }
  }, [runAuthentication])

  const statusLabel = {
    waiting: 'DETECTING',
    touching: 'AWAITING TOUCH',
    verifying: 'AUTHENTICATING',
    success: 'VERIFIED \u2713',
    error: 'FAILED',
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle, #76B93D 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border-t-8 border-yubico-green-400">
        {/* Header */}
        <div className="p-8 text-center border-b border-gray-100">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 bg-yubico-green-400 rounded-full flex items-center justify-center">
              <span className="text-white font-black text-sm">Y</span>
            </div>
            <span className="font-bold text-gray-900">Yubico<sup className="text-xs">&reg;</sup></span>
            <span className="text-gray-400">&times;</span>
            <span className="font-bold text-gray-900">ACME</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Identity</h2>
          <p className="text-sm font-bold uppercase tracking-wider text-yubico-green-500">
            Hardware-Backed Authentication
          </p>
        </div>

        {/* YubiKey Visual */}
        <div className="p-8 bg-gray-50">
          <div className="mb-6">
            <YubiKeyVisual status={status} size="md" />
          </div>

          {/* Status Messages */}
          <AuthStatusDisplay status={status} progress={progress} error={error} />

          {/* Technical Details */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="font-mono font-bold text-gray-500">PROTOCOL</span>
              <span className="font-mono text-yubico-green-500">FIDO2 / WebAuthn</span>
            </div>
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="font-mono font-bold text-gray-500">ENCRYPTION</span>
              <span className="font-mono text-yubico-green-500">RSA-2048</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="font-mono font-bold text-gray-500">STATUS</span>
              <span className="font-mono text-yubico-green-500">{statusLabel[status]}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-white border-t border-gray-100 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <i className="iconoir-lock text-sm"></i>
            <span>Hardware-backed security</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AuthPage
